import React, { Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { organizationApi } from '@app/store';
import { IOrganization } from '@tickeat/types';
import { Button, Form, Panel } from '@app/components';
import { OrganizationForm } from './OrganizationForm';
import { OrganizationFormValues, transformToUpdatableFormOrganization } from './organization-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { OrganizationCard } from './OrganizationCard';
import { rootApi } from '@app/store/api';
import { useAppDispatch } from '@app/store/hooks';
import { OrganizationDTO } from '@app/common';

interface OrganizationUpdateProps {
  organization?: Partial<IOrganization>;
  isFetching: boolean;
}

declare const window: Window;

const CLOSE_TAB_MESSAGE_TYPE = 'close_tab';

export const OrganizationUpdate = ({ organization, isFetching }: OrganizationUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const dispatch = useAppDispatch();

  const [openedTabWindow, setOpenedTabWindow] = useState<Window | null>(null);

  const [update, { isLoading, error: apiErrors }] = organizationApi.useUpdateOneOrganizationsMutation();
  const [connectToHubrise] = organizationApi.useConnectOrganizationToHubriseMutation();

  const formMethods = useForm<OrganizationFormValues>({
    resolver: classValidatorResolver(OrganizationDTO),
    defaultValues: transformToUpdatableFormOrganization(organization)
  });

  useEffect(() => {
    const handle = (event: MessageEvent<{ type: string }>) => {
      if (event.data?.type === CLOSE_TAB_MESSAGE_TYPE) {
        openedTabWindow?.close();

        dispatch(rootApi.util.invalidateTags(['Organizations']));

        toast.success('Votre organisation est désormais connectée à Hubrise.')
      }
    };

    window?.addEventListener("message", handle, false);

    return () => {
      window.removeEventListener("message", handle);
    }
  }, [openedTabWindow])

  const onUpdate = async (formValues: OrganizationFormValues) => {
    try {
      const updatedOrganization = await update(formValues).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedOrganization });

      toast.success('Votre organisation a bien été modifié.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre organisation.')
    }
  }

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormOrganization(organization));
  }, [organization, formMethods.reset]);

  const onDisconnectHubrise = async () => {

  }

  const onConnectHubrise = async () => {
    try {
      const response = await connectToHubrise({}).unwrap();

      const openedTabWindow = window.open(response.authorizationUrl, '_blank');

      setOpenedTabWindow(openedTabWindow);

      openedTabWindow?.focus();
    } catch (error) {
      toast.error("Une erreur est survenue");
    }
  }

  return (
    <Fragment>
      <OrganizationCard
        organization={organization}
        formMethods={formMethods}
        onConnectHubrise={onConnectHubrise}
        onDisconnectHubrise={onDisconnectHubrise} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>

          <Panel.FormContainer>

            <OrganizationForm />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};
